import React, { useCallback } from "react";
import { queryCache } from "react-query";
import { useHistory } from "react-router-dom";
import clsx from "clsx";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import makeStyles from "@material-ui/core/styles/makeStyles";
import CardHeader from "@material-ui/core/CardHeader";
import IconButton from "@material-ui/core/IconButton";
import MenuList from "@material-ui/core/MenuList";
import ListItem from "@material-ui/core/ListItem";
import Popper from "@material-ui/core/Popper";
import Hidden from "@material-ui/core/Hidden";
import Avatar from "@material-ui/core/Avatar";
import Paper from "@material-ui/core/Paper";
import EditIcon from "@material-ui/icons/Edit";
import Card from "@material-ui/core/Card";
import List from "@material-ui/core/List";
import { darken, Drawer } from "@material-ui/core";
import AccessControl from "../../../../components/AccessControl";
import FontIcon from "../../../../components/FontIcon";
import { AdapterLink } from "../../../../components/Adopter";
import { Routes } from "../../../../constants/routes";
import theme from "../../../../themes";
import { useUpdateEmailPreferencesConfig } from "../../../user/userAccount/modals/UpdateEmailPreferences/config";
import { useChangePasswordConfig } from "../../../user/userAccount/modals/ChangePassword/config";
import { useUpdateProfileConfig } from "../../../user/userAccount/modals/EditProfile/config";
import { getCreditConfig } from "../../../student/modals/helpers/getCreditConfig";
import useStudentQueries from "../../../student/hooks/useStudentQueries";
import { UserRole } from "../../../user/enums/UserRole";
import { useAuth } from "../../../auth/hooks/useAuth";
import { useWizard } from "../../../wizard/context";
import { useApp } from "../../hooks/useApp";

const useStyles = makeStyles(() => ({
  menu: {
    padding: 0,
    width: 300,
    [theme.breakpoints.up("md")]: {
      width: 200,
    },
  },
  icon: {
    "& .MuiIcon-root": {
      fontSize: 20,
    },
    "&.MuiIconButton-root": {
      padding: 6,
    },
  },
  openIcon: {
    backgroundColor: "#eeeff3",
    color: "#858997",
    "&:hover": {
      backgroundColor: darken("#eeeff3", 0.1),
      color: "#858997",
    },
  },
  avatarContainer: {
    border: "2px solid #A1A4AC",
    borderRadius: "50%",
  },
  drawerHeader: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
    borderRadius: 0,
    padding: theme.spacing(2),
  },
  drawerHeaderTitle: {
    color: theme.palette.primary.light,
  },
  drawerHeaderSubTitle: {
    color: theme.palette.primary.contrastText,
  },
  drawerHeaderAction: {
    marginTop: theme.spacing(1),
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.light,
  },
  logoutButton: {
    textTransform: "capitalize",
  },
}));

const DashboardRightMenu = () => {
  const classes = useStyles();
  const menuAnchorRef = React.useRef(null);
  const {
    data: { isMenuOpen },
    actions: { toggleMenu },
  } = useApp();
  const {
    user,
    profile,
    logout,
    actions: { refetch },
  } = useAuth();

  const update = async () => {
    await refetch();
    await profile.refetch();
  };

  const onOutsideClicked = useCallback(
    (e) => {
      if (menuAnchorRef.current !== e.currentTarget) {
        e.preventDefault();
        toggleMenu();
      }
    },
    [menuAnchorRef],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const _logout = useCallback(
    (e) => {
      e?.preventDefault();
      return logout(true);
    },
    [logout],
  );

  const profilePhoto = [
    UserRole.Agent,
    UserRole.Parent,
    UserRole.Student,
    UserRole.Teacher,
  ].includes(user?.role)
    ? user?.photo?.preview
    : user?.role === UserRole.School
      ? profile?.data?.data?.school?.image?.preview
      : profile?.data?.data?.serviceProvider?.image?.preview;

  return (
    <>
      <div className={classes.avatarContainer}>
        <IconButton
          ref={menuAnchorRef}
          onMouseUp={toggleMenu}
          className={clsx(classes.icon, isMenuOpen ? classes.openIcon : null)}
        >
          {!profilePhoto?.length ? (
            <FontIcon name="user" />
          ) : (
            <Avatar src={profilePhoto} />
          )}
        </IconButton>
      </div>
      <Hidden smDown>
        <Popper
          transition
          open={isMenuOpen}
          disablePortal
          anchorEl={menuAnchorRef.current}
          placement={"bottom"}
        >
          <Paper className={classes.menu}>
            <ClickAwayListener
              mouseEvent={"onMouseUp"}
              onClickAway={onOutsideClicked}
            >
              <MenuList autoFocusItem={isMenuOpen}>
                <MenuItems
                  handleClose={toggleMenu}
                  handleLogout={_logout}
                  user={user}
                  refetch={update}
                />
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Popper>
      </Hidden>
      <Hidden mdUp>
        <Drawer
          open={isMenuOpen}
          variant={"temporary"}
          anchor={"right"}
          onClose={toggleMenu}
        >
          {user && (
            <Card className={classes.drawerHeader}>
              <CardHeader
                title={"Hello"}
                subheader={user.name}
                titleTypographyProps={{ className: classes.drawerHeaderTitle }}
                subheaderTypographyProps={{
                  className: classes.drawerHeaderSubTitle,
                }}
                avatar={
                  !profilePhoto?.length ? (
                    <FontIcon name="user" />
                  ) : (
                    <Avatar src={profilePhoto} />
                  )
                }
                action={
                  <AccessControl
                    allowedRoles={[
                      UserRole.Owner,
                      UserRole.Admin,
                      UserRole.Support,
                    ]}
                    verified={false}
                  >
                    <IconButton
                      component={AdapterLink}
                      to={`/${Routes.MY_ACCOUNT}`}
                      color={"inherit"}
                      onClick={toggleMenu}
                      className={classes.drawerHeaderAction}
                    >
                      <EditIcon fontSize={"small"} color={"inherit"} />
                    </IconButton>
                  </AccessControl>
                }
              />
            </Card>
          )}
          <List className={classes.menu}>
            <MenuItems
              handleClose={toggleMenu}
              handleLogout={_logout}
              user={user}
              refetch={update}
            />
          </List>
        </Drawer>
      </Hidden>
    </>
  );
};

const MenuItems = React.memo(
  ({
    handleClose,
    handleLogout,
    user,
    refetch,
  }: {
    handleClose: any;
    handleLogout: any;
    user: object;
    refetch: any;
  }) => {
    const { openWizard } = useWizard();
    const changePasswordConfig = useChangePasswordConfig();
    const updateProfileConfig = useUpdateProfileConfig();
    const updateEmailPreferencesConfig = useUpdateEmailPreferencesConfig();
    const { fetchCredits } = useStudentQueries();
    const history = useHistory();

    const handleSignOutClick = async (e) => {
      e.persist();
      handleClose(e);

      const isConfirmed = await openWizard({
        steps: [
          {
            titleConfig: {
              titleText: "Sign Out",
            },
            content: "Are you sure you want to sign out?",
          },
        ],
        submitButtonText: "Proceed",
        cancelButtonText: "Close",
      });

      if (isConfirmed) {
        handleLogout(e);
        await queryCache.invalidateQueries();
      }
    };

    const handleChangePassword = async (e) => {
      e.persist();
      handleClose();

      const result = await openWizard(changePasswordConfig);

      if (result) {
        setTimeout(() => {
          handleLogout();
        }, 5000);
      }
    };

    const handleUpdateEmailPreferences = async (e) => {
      e.persist();
      handleClose();

      await openWizard(updateEmailPreferencesConfig);
    };

    const handleUpdateProfile = async (e) => {
      e.persist();
      handleClose();

      (await openWizard(updateProfileConfig)) && (await refetch());
    };

    const handleViewCredits = async (e) => {
      e.persist();
      handleClose(e);

      const {
        data: { availableCredits, package: currentPackage },
      } = await fetchCredits();

      if (
        await openWizard(getCreditConfig({ availableCredits, currentPackage }))
      ) {
        history.push(`/${Routes.EXTRA_SERVICES}?pack=2`);
      }
    };

    return (
      <>
        <AccessControl verified={false} keepEmpty={true}>
          <ListItem button onClick={handleUpdateProfile}>
            Edit Profile
          </ListItem>
          <ListItem button onClick={handleChangePassword}>
            Change Password
          </ListItem>
          <ListItem button onClick={handleUpdateEmailPreferences}>
            Email Preferences
          </ListItem>
        </AccessControl>
        <AccessControl allowedRoles={[UserRole.Student, UserRole.Parent]}>
          <ListItem button onClick={handleViewCredits}>
            Credits
          </ListItem>
        </AccessControl>
        {/* <AccessControl allowedRoles={[4]}>
                <ListItem
                    button
                    onClick={handleClose}
                    component={AdapterLink}
                    to="/extra-services"
                >Extra Services</ListItem>
            </AccessControl> */}
        <ListItem
          component={AdapterLink}
          href="/donate"
          button
          onClick={handleClose}
        >
          Donate
        </ListItem>
        <AccessControl allowedRoles={[UserRole.Agent, UserRole.Student]}>
          <ListItem
            component={AdapterLink}
            href="/apply-for-financial-aid"
            button
            onClick={handleClose}
          >
            Apply for Financial Aid
          </ListItem>
        </AccessControl>
        <ListItem
          component={AdapterLink}
          href="/faq"
          button
          onClick={handleClose}
        >
          F.A.Q.
        </ListItem>
        <ListItem
          component={AdapterLink}
          button
          onClick={handleClose}
          href="/privacy-policy"
        >
          Privacy Policy
        </ListItem>
        <ListItem
          component={AdapterLink}
          button
          onClick={handleClose}
          href="/terms-and-conditions"
        >
          Terms And Conditions
        </ListItem>
        {user && (
          <ListItem button onClick={handleSignOutClick}>
            Sign Out
          </ListItem>
        )}
      </>
    );
  },
);

export default React.memo(DashboardRightMenu);
